import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import './Leaderboard.scss';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGame, setSelectedGame] = useState('all');
  const [lastCalculated, setLastCalculated] = useState(null);

  const gameOptions = [
    { value: 'all', label: 'All Games' },
    { value: 'slay-the-spire', label: 'Slay the Spire' },
    { value: 'bible-books', label: 'Bible Books' },
    { value: 'subnautica-creatures', label: 'Subnautica Creatures' },
    { value: 'aoe2-units', label: 'Age of Empires II' },
    { value: 'pokemon', label: 'Pokemon' }
  ];

  const fetchLeaderboard = async (game = selectedGame) => {
    setLoading(true);
    setError(null);

    try {
      let url;
      if (game === 'all') {
        // Use real-time endpoint for "all games" - shows accurate current streaks
        url = `/api/leaderboard/real-time?limit=10`;
      } else {
        // Use real-time endpoint for specific games - shows accurate current streaks
        url = `/api/leaderboard/real-time/${game}?limit=10`;
      }

      const response = await apiService.get(url);
      const data = await response.json();
      setLeaderboardData(data.users);
      
      // Set timestamp for real-time calculations (all endpoints now provide this)
      setLastCalculated(data.calculatedAt);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGameChange = (newGame) => {
    setSelectedGame(newGame);
    fetchLeaderboard(newGame);
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';  
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 10 }, (_, index) => (
      <tr key={`skeleton-${index}`} className="leaderboard__row leaderboard__row--skeleton">
        <td className="leaderboard__cell leaderboard__cell--rank">
          <div className="leaderboard__skeleton"></div>
        </td>
        <td className="leaderboard__cell leaderboard__cell--username">
          <div className="leaderboard__skeleton leaderboard__skeleton--username"></div>
        </td>
        <td className="leaderboard__cell leaderboard__cell--current-streak">
          <div className="leaderboard__skeleton"></div>
        </td>
        <td className="leaderboard__cell leaderboard__cell--best-streak">
          <div className="leaderboard__skeleton"></div>
        </td>
        <td className="leaderboard__cell leaderboard__cell--daily-games">
          <div className="leaderboard__skeleton"></div>
        </td>
        <td className="leaderboard__cell leaderboard__cell--total-games">
          <div className="leaderboard__skeleton"></div>
        </td>
      </tr>
    ));
  };

  const renderEmptyRow = (index) => (
    <tr key={`empty-${index}`} className="leaderboard__row leaderboard__row--empty">
      <td className="leaderboard__cell leaderboard__cell--rank">-</td>
      <td className="leaderboard__cell leaderboard__cell--username">-</td>
      <td className="leaderboard__cell leaderboard__cell--current-streak">-</td>
      <td className="leaderboard__cell leaderboard__cell--best-streak">-</td>
      <td className="leaderboard__cell leaderboard__cell--daily-games">-</td>
      <td className="leaderboard__cell leaderboard__cell--total-games">-</td>
    </tr>
  );

  const renderTableRows = () => {
    if (loading) {
      return renderSkeletonRows();
    }

    if (leaderboardData.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="leaderboard__empty-cell">
            <div className="leaderboard__empty">
              <h3>No players found</h3>
              <p>No players have won any daily games for this category yet.</p>
            </div>
          </td>
        </tr>
      );
    }

    const rows = [];
    
    // Add real data rows
    leaderboardData.forEach((user, index) => {
      const rank = index + 1;
      rows.push(
        <tr key={user.id} className="leaderboard__row">
          <td className={`leaderboard__cell leaderboard__cell--rank ${rank <= 3 ? 'medal' : ''}`}>
            {getRankDisplay(rank)}
          </td>
          <td className="leaderboard__cell leaderboard__cell--username">
            <button 
              className="leaderboard__username-link"
              onClick={() => navigate(`/profile/${user.username}`)}
            >
              {user.username}
            </button>
          </td>
          <td className="leaderboard__cell leaderboard__cell--current-streak">
            {user.current_streak}
          </td>
          <td className="leaderboard__cell leaderboard__cell--best-streak">
            {user.best_streak}
          </td>
          <td className="leaderboard__cell leaderboard__cell--daily-games">
            {user.total_daily_games_played}
          </td>
          <td className="leaderboard__cell leaderboard__cell--total-games">
            {user.total_games_played}
          </td>
        </tr>
      );
    });

    // Fill remaining rows with empty placeholders to always have 10 rows
    const remainingRows = 10 - leaderboardData.length;
    for (let i = 0; i < remainingRows; i++) {
      rows.push(renderEmptyRow(i));
    }

    return rows;
  };

  if (error) {
    return (
      <div className="leaderboard">
        <div className="leaderboard__container">
          <div className="leaderboard__error">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard">
      <div className="leaderboard__container">
        <div className="leaderboard__header">
          <h1>Leaderboard</h1>
          <p>Top players ranked by current streak</p>
        </div>

        <div className="leaderboard__controls">
          <div className="leaderboard__filter-group">
            <div className="leaderboard__game-filter">
              <label htmlFor="game-select">Game:</label>
              <select
                id="game-select"
                value={selectedGame}
                onChange={(e) => handleGameChange(e.target.value)}
                className="leaderboard__select"
              >
                {gameOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {lastCalculated && (
              <div className="leaderboard__calculation-info">
                <div className="leaderboard__timestamp">
                  Calculated: {new Date(lastCalculated).toLocaleTimeString('en-US', { 
                    timeZoneName: 'short',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="leaderboard__table-container">
          <table className="leaderboard__table">
            <thead>
              <tr>
                <th className="leaderboard__header-cell">Rank</th>
                <th className="leaderboard__header-cell">Username</th>
                <th className="leaderboard__header-cell">Current Streak</th>
                <th className="leaderboard__header-cell">Best Streak</th>
                <th className="leaderboard__header-cell">Daily Games</th>
                <th className="leaderboard__header-cell">Total Games</th>
              </tr>
            </thead>
            <tbody>
              {renderTableRows()}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default Leaderboard;